import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type {
  Country,
  Province,
  ProvinceData,
  LocalAuthority,
  LocalAuthorityData,
  ReservoirZone,
  ReservoirZoneData,
  Suburb,
  SuburbData,
  Address,
  AddressData,
  DistrictMeteredArea,
  DistrictMeteredAreaData,
  PlaceState,
} from "@/types";
import axios, { AxiosError, AxiosHeaderValue } from 'axios';
import {
    countriesURL,
    provincesURL,
    deletedProvincesURL,
    countryProvincesURL,
    createProvinceURL,
    updateProvinceURL,
    deleteProvinceURL,
    activateProvinceURL,
    localAuthoritiesURL,
    deletedLocalAuthoritiesURL,
    createLocalAuthorityURL,
    updateLocalAuthorityURL,
    deleteLocalAuthorityURL,
    activateLocalAuthorityURL ,
    districtMeteredAreasURL,
    deletedDistrictMeteredAreasURL,
    createDistrictMeteredAreaURL,
    updateDistrictMeteredAreaURL,
    deleteDistrictMeteredAreaURL,
    activateDistrictMeteredAreaURL,
    reservoirZonesURL,
    deletedReservoirZonesURL,
    activateReservoirZoneURL,
    createReservoirZoneURL,
    updateReservoirZoneURL,
    deleteReservoirZoneURL,
    suburbsURL,
    deletedSuburbsURL,
    createSuburbURL,
    updateSuburbURL,
    deleteSuburbURL,
    activateSuburbURL,
    addressesURL ,
    deletedAddressesURL,
    createAddressURL,
    updateAddressURL ,
    deleteAddressURL,
    activateAddressURL,
} from "../../constants";
import {destroyToten} from '../authentications/authenticationsSlice'


interface ErrorMessage {
  error: string;
  message: string;
}



interface getPlacesArgsType {
  token: string | null;
}

interface getProvincesArgsType {
  token: string | null;
  payload:{countryId: string | undefined| null}
}

interface createProvinceDataType {
  payload: ProvinceData;
  token: string | null;
}

interface updateProvinceDataType {
  payload: Province;
  token: string | null;
}

interface createLocalAuthorityDataType {
  payload: LocalAuthorityData;
  token: string | null;
}

interface updateLocalAuthorityDataType {
  payload: LocalAuthority;
  token: string | null;
}

interface createReservoirZoneDataType {
  payload: ReservoirZoneData;
  token: string | null;
}

interface updateReservoirZoneDataType {
  payload: ReservoirZone;
  token: string | null;
}

interface createSuburbDataType {
  payload: SuburbData;
  token: string | null;
}

interface updateSuburbDataType {
  payload: Suburb;
  token: string | null;
}

interface createAddressDataType {
  payload: AddressData;
  token: string | null;
}

interface updateAddressDataType {
  payload: Address;
  token: string | null;
}

interface createDistrictMeteredAreaDataType {
  payload: DistrictMeteredAreaData;
  token: string | null;
}

interface updateDistrictMeteredAreaDataType {
  payload: DistrictMeteredArea;
  token: string | null;
}




export const provinceInitialValues: ProvinceData = {
    name: "",
    country: "",
    countryId: "",
}

export const localAuthorityInitialValues: LocalAuthorityData = {
    name: "",
    country: "",
    countryId: "",
    provinceId: "",
    province: "",
}


export const reservoirZoneInitialValues: ReservoirZoneData = {
    name: "",
    description: "",
    localAuthorityId: "",
    localAuthority: "",
    reservoirCount: 0.0,
    centroidLat: 0.0,
    centroidLon: 0.0,
}


export const districtMeteredAreaInitialValues: DistrictMeteredAreaData = {
    name: "",
    localAuthorityId: "",
    localAuthority: "",
    zoneId: "",
    zone: "",
    description: "",
    centroidLat: 0.0,
    centroidLon: 0.0,
    pressureZone: "",
}

export const suburbInitialValues: SuburbData = {
    name: "",
    localAuthorityId: "",
    localAuthority: "",
    zoneId: "",
    zone: "",
    districtMeteredAreaId: "",
    districtMeteredArea: "",
    longitude: 0,
    latitude: 0,
}

export const addressInitialValues: AddressData = {
    fullAddress: "",
    suburbId: "",
    suburb: "",
    zoneId: "",
    zone: "",
    localAuthorityId: "",
    localAuthority: "",
    longitude: 0,
    latitude: 0,
    type: "",
}


export const initialState: PlaceState = {
  getStatus: 'idle',
  createPlaceStatus: 'idle',
  error: null,
  message: null,
  countries: [],
  provinces: [],
  localAuthorities: [],
  reservoirZones: [],
  districtMeteredAreas: [],
  suburbs: [],
  addresses: [],
}


export const getCountries = createAsyncThunk<Country[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getCountries",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(countriesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const createProvince = createAsyncThunk<Province, createProvinceDataType, { rejectValue: ErrorMessage }>(
  "places/createProvince",
  async (data:createProvinceDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createProvinceURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateProvince = createAsyncThunk<Province, updateProvinceDataType, { rejectValue: ErrorMessage }>(
  "places/updateProvince",
  async (data:updateProvinceDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateProvinceURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteProvince = createAsyncThunk<Province, updateProvinceDataType, { rejectValue: ErrorMessage }>(
  "places/deleteProvince",
  async (data:updateProvinceDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteProvinceURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateProvince = createAsyncThunk<Province, updateProvinceDataType, { rejectValue: ErrorMessage }>(
  "places/activateProvince",
  async (data:updateProvinceDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateProvinceURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedProvinces = createAsyncThunk<Province[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getDeletedProvinces",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedProvincesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getProvinces = createAsyncThunk<Province[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getProvinces",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(provincesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getCountryProvinces = createAsyncThunk<Province[], getProvincesArgsType, { rejectValue: ErrorMessage }>(
  "places/getCountryProvinces",
  async (data: getProvincesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     let params = data?.payload;
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(countryProvincesURL, {params});
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createLocalAuthority = createAsyncThunk<LocalAuthority, createLocalAuthorityDataType, { rejectValue: ErrorMessage }>(
  "places/createLocalAuthority",
  async (data:createLocalAuthorityDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createLocalAuthorityURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateLocalAuthority = createAsyncThunk<LocalAuthority, updateLocalAuthorityDataType, { rejectValue: ErrorMessage }>(
  "places/updateLocalAuthority",
  async (data:updateLocalAuthorityDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateLocalAuthorityURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteLocalAuthority = createAsyncThunk<LocalAuthority, updateLocalAuthorityDataType, { rejectValue: ErrorMessage }>(
  "places/deleteLocalAuthority",
  async (data:updateLocalAuthorityDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteLocalAuthorityURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateLocalAuthority = createAsyncThunk<LocalAuthority, updateLocalAuthorityDataType, { rejectValue: ErrorMessage }>(
  "places/activateLocalAuthority",
  async (data:updateLocalAuthorityDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateLocalAuthorityURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedLocalAuthorities = createAsyncThunk<LocalAuthority[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getDeletedLocalAuthorities",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedLocalAuthoritiesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getLocalAuthorities = createAsyncThunk<LocalAuthority[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getLocalAuthorities",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(localAuthoritiesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const createReservoirZone = createAsyncThunk<ReservoirZone, createReservoirZoneDataType, { rejectValue: ErrorMessage }>(
  "places/createReservoirZone",
  async (data:createReservoirZoneDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createReservoirZoneURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateReservoirZone = createAsyncThunk<ReservoirZone, updateReservoirZoneDataType, { rejectValue: ErrorMessage }>(
  "places/updateReservoirZone",
  async (data:updateReservoirZoneDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateReservoirZoneURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteReservoirZone = createAsyncThunk<ReservoirZone, updateReservoirZoneDataType, { rejectValue: ErrorMessage }>(
  "places/deleteReservoirZone",
  async (data:updateReservoirZoneDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteReservoirZoneURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateReservoirZone = createAsyncThunk<ReservoirZone, updateReservoirZoneDataType, { rejectValue: ErrorMessage }>(
  "places/activateReservoirZone",
  async (data:updateReservoirZoneDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateReservoirZoneURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedReservoirZones = createAsyncThunk<ReservoirZone[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getDeletedReservoirZones",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedReservoirZonesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getReservoirZones = createAsyncThunk<ReservoirZone[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getReservoirZones",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(reservoirZonesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const createDistrictMeteredArea = createAsyncThunk<DistrictMeteredArea, createDistrictMeteredAreaDataType, { rejectValue: ErrorMessage }>(
  "places/createDistrictMeteredArea",
  async (data:createDistrictMeteredAreaDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createDistrictMeteredAreaURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateDistrictMeteredArea = createAsyncThunk<DistrictMeteredArea, updateDistrictMeteredAreaDataType, { rejectValue: ErrorMessage }>(
  "places/updateDistrictMeteredArea",
  async (data:updateDistrictMeteredAreaDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateDistrictMeteredAreaURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteDistrictMeteredArea = createAsyncThunk<DistrictMeteredArea, updateDistrictMeteredAreaDataType, { rejectValue: ErrorMessage }>(
  "places/deleteDistrictMeteredArea",
  async (data:updateDistrictMeteredAreaDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteDistrictMeteredAreaURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateDistrictMeteredArea = createAsyncThunk<DistrictMeteredArea, updateDistrictMeteredAreaDataType, { rejectValue: ErrorMessage }>(
  "places/activateDistrictMeteredArea",
  async (data:updateDistrictMeteredAreaDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateDistrictMeteredAreaURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedDistrictMeteredAreas = createAsyncThunk<DistrictMeteredArea[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getDeletedDistrictMeteredAreas",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedDistrictMeteredAreasURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDistrictMeteredAreas = createAsyncThunk<DistrictMeteredArea[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getDistrictMeteredAreas",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(districtMeteredAreasURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const createSuburb = createAsyncThunk<Suburb, createSuburbDataType, { rejectValue: ErrorMessage }>(
  "places/createSuburb",
  async (data:createSuburbDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createSuburbURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateSuburb = createAsyncThunk<Suburb, updateSuburbDataType, { rejectValue: ErrorMessage }>(
  "places/updateSuburb",
  async (data:updateSuburbDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateSuburbURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteSuburb = createAsyncThunk<Suburb, updateSuburbDataType, { rejectValue: ErrorMessage }>(
  "places/deleteSuburb",
  async (data:updateSuburbDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteSuburbURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateSuburb = createAsyncThunk<Suburb, updateSuburbDataType, { rejectValue: ErrorMessage }>(
  "places/activateSuburb",
  async (data:updateSuburbDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateSuburbURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedSuburbs = createAsyncThunk<Suburb[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getDeletedSuburbs",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedSuburbsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getSuburbs = createAsyncThunk<Suburb[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getSuburbs",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(suburbsURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);

//
export const createAddress = createAsyncThunk<Address, createAddressDataType, { rejectValue: ErrorMessage }>(
  "places/createAddress",
  async (data:createAddressDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        createAddressURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const updateAddress = createAsyncThunk<Address, updateAddressDataType, { rejectValue: ErrorMessage }>(
  "places/updateAddress",
  async (data:updateAddressDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        updateAddressURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const deleteAddress = createAsyncThunk<Address, updateAddressDataType, { rejectValue: ErrorMessage }>(
  "places/deleteAddress",
  async (data:updateAddressDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        deleteAddressURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const activateAddress = createAsyncThunk<Address, updateAddressDataType, { rejectValue: ErrorMessage }>(
  "places/activateAddress",
  async (data:updateAddressDataType, thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data.token,
     };
     let payload = data.payload
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.post(
        activateAddressURL,
        payload
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getDeletedAddresses = createAsyncThunk<Address[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getDeletedAddresses",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(deletedAddressesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const getAddresses = createAsyncThunk<Address[], getPlacesArgsType, { rejectValue: ErrorMessage }>(
  "places/getAddresses",
  async (data: getPlacesArgsType , thunkAPI) => {
    try {
      const headers: { [key: string]: AxiosHeaderValue } = {
       'Content-Type': 'application/json',
       token: data?.token,
     };
     axios.defaults.headers = Object.assign({},axios.defaults.headers,headers);
      const response = await axios.get(addressesURL);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const message: ErrorMessage = {
        error:"error",
        message: (axiosError.response?.data as any).detail?.message
      };
      if(message.message ==='destroyToken'){
        thunkAPI.dispatch(destroyToten())
      }

      return thunkAPI.rejectWithValue(message);
    }
  }
);




export const placesSlice = createSlice({
  name: 'places',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCountries.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getCountries.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.countries = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getCountries.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.countries = [];
      })
      .addCase(getProvinces.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getProvinces.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.provinces = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getProvinces.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.provinces = [];
      })
      .addCase(getDeletedProvinces.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedProvinces.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.provinces = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedProvinces.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.provinces = [];
      })
      .addCase(getCountryProvinces.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getCountryProvinces.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.provinces = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getCountryProvinces.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.provinces = [];
      })

      //suburbs
      .addCase(createProvince.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createProvince.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Province Created";
      })
      .addCase(createProvince.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateProvince.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateProvince.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Province Updated";
      })
      .addCase(updateProvince.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteProvince.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteProvince.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Province Deleted";
      })
      .addCase(deleteProvince.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateProvince.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateProvince.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Province Restored";
      })
      .addCase(activateProvince.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      //local authorites 
      .addCase(getLocalAuthorities.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getLocalAuthorities.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.localAuthorities = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getLocalAuthorities.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.localAuthorities = [];
      })
      .addCase(getDeletedLocalAuthorities.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedLocalAuthorities.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.localAuthorities = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedLocalAuthorities.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.localAuthorities = [];
      })
      .addCase(createLocalAuthority.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createLocalAuthority.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Local Authority Created";
      })
      .addCase(createLocalAuthority.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateLocalAuthority.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateLocalAuthority.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Local Authority Updated";
      })
      .addCase(updateLocalAuthority.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteLocalAuthority.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteLocalAuthority.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Local Authority Deleted";
      })
      .addCase(deleteLocalAuthority.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateLocalAuthority.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateLocalAuthority.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Local Authority Restored";
      })
      .addCase(activateLocalAuthority.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })

      .addCase(getReservoirZones.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getReservoirZones.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.reservoirZones = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getReservoirZones.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.reservoirZones = [];
      })
      .addCase(getDeletedReservoirZones.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedReservoirZones.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.reservoirZones = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedReservoirZones.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.reservoirZones = [];
      })
      .addCase(createReservoirZone.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createReservoirZone.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Reservoir Zone Created";
      })
      .addCase(createReservoirZone.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateReservoirZone.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateReservoirZone.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Reservoir Zone Updated";
      })
      .addCase(updateReservoirZone.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteReservoirZone.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteReservoirZone.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Reservoir Zone Deleted";
      })
      .addCase(deleteReservoirZone.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateReservoirZone.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateReservoirZone.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Reservoir Zone Restored";
      })
      .addCase(activateReservoirZone.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })

      .addCase(getDistrictMeteredAreas.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDistrictMeteredAreas.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.districtMeteredAreas = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDistrictMeteredAreas.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.districtMeteredAreas = [];
      })
      .addCase(getDeletedDistrictMeteredAreas.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedDistrictMeteredAreas.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.districtMeteredAreas = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedDistrictMeteredAreas.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.districtMeteredAreas = [];
      })
      .addCase(createDistrictMeteredArea.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createDistrictMeteredArea.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "District Metered Area Created";
      })
      .addCase(createDistrictMeteredArea.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateDistrictMeteredArea.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateDistrictMeteredArea.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "District Metered Area Updated";
      })
      .addCase(updateDistrictMeteredArea.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteDistrictMeteredArea.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteDistrictMeteredArea.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "District Metered Area Deleted";
      })
      .addCase(deleteDistrictMeteredArea.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateDistrictMeteredArea.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateDistrictMeteredArea.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "District Metered Area Restored";
      })
      .addCase(activateDistrictMeteredArea.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })

      //
      .addCase(getSuburbs.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getSuburbs.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.suburbs = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getSuburbs.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.suburbs = [];
      })
      .addCase(getDeletedSuburbs.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedSuburbs.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.suburbs = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedSuburbs.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.suburbs = [];
      })
      .addCase(createSuburb.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createSuburb.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Suburb Created";
      })
      .addCase(createSuburb.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateSuburb.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateSuburb.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Suburb Updated";
      })
      .addCase(updateSuburb.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteSuburb.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteSuburb.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Suburb Deleted";
      })
      .addCase(deleteSuburb.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateSuburb.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateSuburb.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Suburb Restored";
      })
      .addCase(activateSuburb.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })

      //
      .addCase(getAddresses.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getAddresses.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.addresses = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getAddresses.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.addresses = [];
      })
      .addCase(getDeletedAddresses.pending, (state) => {
        state.getStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedAddresses.fulfilled, (state, action) => {
        state.getStatus = 'succeeded';
        state.addresses = action.payload;
        state.error = null;
        state.message = null;
      })
      .addCase(getDeletedAddresses.rejected, (state, action) => {
        state.getStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        state.addresses = [];
      })
      .addCase(createAddress.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Address Created";
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(updateAddress.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Address Updated";
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
      })
      .addCase(deleteAddress.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Address Deleted";
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error"
        
      })
      .addCase(activateAddress.pending, (state) => {
        state.createPlaceStatus = 'loading';
        state.error = null;
        state.message = null;
      })
      .addCase(activateAddress.fulfilled, (state, action) => {
        state.createPlaceStatus = 'succeeded';
        state.error = null;
        state.message = "Address Restored";
      })
      .addCase(activateAddress.rejected, (state, action) => {
        state.createPlaceStatus = 'failed';
        state.message = action.error.message || 'Something went wrong';
        state.error = "error" 
      })

    }
})

export default placesSlice.reducer
